import React from "react";
import { Badge } from "./Badge";
import {
  getPostedStatusLabel,
  getStoredAccountingBackend,
} from "../../config/accounting";

type DocumentStatus = string;
type ReviewStatus = string;

interface StatusBadgeForInvoicesAndBillsProps {
  status: DocumentStatus;
  reviewed: ReviewStatus;
  type?: "invoice" | "bill";
  onHold?: number; // 0: not on hold, 1: on hold
  /** When true, always show "Under Review" (e.g. in Under Review section tables) */
  forceUnderReview?: boolean;
}

export const StatusBadgeForInvoicesAndBills: React.FC<
  StatusBadgeForInvoicesAndBillsProps
> = ({
  status,
  reviewed,
  type = "invoice",
  onHold = 0,
  forceUnderReview = false,
}) => {
  const postedLabel = getPostedStatusLabel(getStoredAccountingBackend());

  // Helper function to get status badge variant
  const getStatusBadgeVariant = (
    status: DocumentStatus,
    reviewed: ReviewStatus,
    onHold: number
  ): "neutral" | "warning" | "success" | "error" => {
    // On Hold
    if (onHold === 1) {
      return "neutral";
    }
    // Pending
    else if ((status === "0" || status === "1") && reviewed === "0") {
      return "warning";
    }
    // Posted
    else if (status === "1" && reviewed === "1") {
      return "success";
    }
    // Duplicate/Rejected
    else if (status === "3" || status === "4") {
      return "error";
    }
    // Completed
    else if (status === "2") {
      return "success";
    }
    // Failed
    else if (status === "10") {
      return "error";
    }
    return "neutral";
  };

  // Helper function to get badge custom className for text color override
  const getBadgeClassName = (
    status: DocumentStatus,
    reviewed: ReviewStatus,
    onHold: number
  ): string => {
    if (onHold === 1) {
      return "[&>span]:!text-subtext-color";
    }
    if (status === "1" && reviewed === "1") {
      return "[&>span]:!text-success-800";
    }
    if (status === "3" || status === "4") {
      return "[&>span]:!text-error-600";
    }
    return "";
  };

  // Helper function to get status label
  const getStatusLabel = (
    status: DocumentStatus,
    reviewed: ReviewStatus,
    onHold: number
  ): string => {
    if (onHold === 1) {
      return "On Hold";
    } else if ((status === "0" || status === "1") && reviewed === "0") {
      return "Pending";
    } else if (status === "1" && reviewed === "1") {
      return postedLabel;
    } else if (status === "2") {
      return "Completed";
    } else if (status === "3") {
      return "Duplicate";
    } else if (status === "4") {
      return "Rejected";
    } else if (status === "10") {
      return "Failed";
    }
    return "Unknown";
  };

  const variant = forceUnderReview
    ? "warning"
    : getStatusBadgeVariant(status, reviewed, onHold);
  const label = forceUnderReview
    ? "Under Review"
    : getStatusLabel(status, reviewed, onHold);
  const className = forceUnderReview
    ? ""
    : getBadgeClassName(status, reviewed, onHold);

  return (
    <Badge variant={variant} className={className}>
      {label}
    </Badge>
  );
};
